import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ExpensesByCategoryData } from '../types';
import { formatCurrency } from '../utils/formatters';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ExpenseChartProps {
  categoryData: ExpensesByCategoryData | null;
  selectedMonth?: Date;
  loading?: boolean;
}

const COLORS = [
  '#5B21B6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#14B8A6', '#84CC16', '#F97316', '#EC4899'
];

const ExpensesByCategoryChart: React.FC<ExpenseChartProps> = ({ categoryData, selectedMonth, loading = false }) => {
  // Show loading state only if data is null (still loading)
  if (!categoryData || loading) {
    return (
      <div className="card">
        <div className="card-content">
          <h2 className="heading-2">
            {selectedMonth ?
              `${selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Overview` :
              'Monthly Overview'
            }
          </h2>
          <div className="chart-container">
            <div className="loading">
              <div className="spinner" />
              <div style={{ marginLeft: 'var(--spacing-md)', color: 'var(--text-light)' }}>
                Loading your expense overview...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if data is loaded but no categories
  if (!categoryData.expenses || categoryData.expenses.length === 0) {
    return (
      <div className="card">
        <div className="card-content">
          <h2 className="heading-2">
            {selectedMonth ?
              `${selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Overview` :
              'Monthly Overview'
            }
          </h2>
          <div className="chart-container">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--spacing-xl)',
              color: 'var(--text-light)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-md)' }}>📊</div>
              <div style={{ fontSize: '16px', marginBottom: 'var(--spacing-sm)' }}>No expenses found</div>
              <div style={{ fontSize: '14px' }}>
                {selectedMonth ?
                  `No expenses recorded for ${selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` :
                  'Start tracking your expenses to see insights'
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show only parent categories in the chart (with combined totals from backend)
  const parentCategories = categoryData.expenses.filter(cat => cat.is_parent);

  // Sort by total for consistent ordering (already sorted from backend, but ensure consistency)
  const sortedCategories = parentCategories
    .sort((a, b) => b.total - a.total)
    .slice(0, 10); // Limit to top 10 for chart readability

  const chartData = {
    labels: sortedCategories.map(cat => cat.category),
    datasets: [
      {
        data: sortedCategories.map(cat => cat.total),
        backgroundColor: COLORS.slice(0, sortedCategories.length),
        borderWidth: 0,
        hoverOffset: 8,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#ffffff',
        titleColor: '#1a1a1a',
        bodyColor: '#4a4a4a',
        borderColor: 'rgba(0, 0, 0, 0.15)',
        borderWidth: 2,
        cornerRadius: 8,
        displayColors: false,
        padding: 10,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        bodyFont: {
          size: 13,
          weight: 'bold',
          family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        titleFont: {
          size: 11,
          weight: 'normal',
          family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        titleMarginBottom: 6,
        z: 9999,
        callbacks: {
          title: function(context: any) {
            return context[0].label || '';
          },
          label: function(context: any) {
            const value = context.raw;
            const total = categoryData.total;
            const percentage = ((value / total) * 100).toFixed(1);
            const currency = categoryData.currency || 'USD';
            return `${formatCurrency(value, currency)} (${percentage}%)`;
          }
        },
      },
    },
    cutout: '65%',
    elements: {
      arc: {
        borderRadius: 6,
        borderWidth: 0,
        hoverBorderWidth: 0,
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    onHover: (event: any, activeElements: any) => {
      (event.native.target as HTMLElement).style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
  };

  const total = categoryData.total;
  const currency = categoryData.currency || 'USD';

  return (
    <div className="chart-container">
      <Doughnut data={chartData} options={options} />
      <div className="chart-center-text">
        <div className="text-light">Total Spent</div>
        <div className="chart-total-amount">
          {formatCurrency(total, currency)}
        </div>
      </div>
    </div>
  );
};

export default ExpensesByCategoryChart;
